import { Col, Form, Modal, Row } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { Formik } from 'formik'
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import TextInput from '../../../widgets/components/Input/TextInput'
import { InfoCard2 } from '../../../widgets/components/UI/InfoCard2'
import { toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { StorageDirectoryTable } from './storageDirectoryTable'
import { useGoogleLogin } from '@react-oauth/google'
import {
  createSourceSync,
  selectedFilesToSyncRecordsKeeper,
  updateSourceSync,
} from '../../core/_requests'
import {
  cloudRegionTypes,
  CloudStorageIntegrationTypes,
  OauthIntegrationTypes,
  RecordSync,
} from '../../core/_constants'
import { useAuth } from '../../../auth'
import { SelectedFileOrFolderId, StorageType } from '../../core/_models'
import { useSearchParams } from 'react-router-dom'
import * as Yup from 'yup'
import { TiTick } from 'react-icons/ti'
import { toast } from 'react-toastify'
import brandingConfig from '../../../../../config/brandingConfig'
import TextArea from '../../../widgets/components/Input/TextArea'
import { FileUpload } from '../../../widgets/components/FileUpload'
import SelectSearchInput from '../../../widgets/components/Input/SelectSearchInput'

const AddStorageModal = ({
  isActive,
  setIsActive,
  fetchSources,
  isReconnectClicked,
  reconnectSourceIsClicked,
  setCurrentDocumentStep,
  currentDocumentStep,
  reconnectSourceIdIsClicked,
  reconnectSourceNameIsClicked,
  setIsReconnectClicked,
}: {
  isActive: boolean
  setIsActive: Dispatch<SetStateAction<boolean>>
  fetchSources: () => void
  isReconnectClicked: boolean
  setIsReconnectClicked: Dispatch<SetStateAction<boolean>>
  reconnectSourceIsClicked: RecordSync | null
  setCurrentDocumentStep: Dispatch<SetStateAction<number>>
  currentDocumentStep: number
  reconnectSourceIdIsClicked: string | null
  reconnectSourceNameIsClicked: string | null
}) => {
  const { formatMessage } = useIntl()
  const [selectedCard, setSelectedCard] = useState<StorageType | undefined>()
  const formikRef = useRef<any>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const code = searchParams.get('code')
  const { companyId } = useAuth()
  const [storageDataName, setStorageDataName] = useState<string | undefined>()
  const [sourceId, setSourceId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedFileOrFolderId, setSelectedFileOrFolderId] =
    useState<SelectedFileOrFolderId | null>(null)
  const [parentFolder, setParentFolder] = useState<{ id: string; name: string } | null>(null)
  const [folderPath, setFolderPath] = useState<{ id: string; name: string }[]>([])
  const [openedFolderId, setOpenedFolderId] = useState<string | null>('rootFolder')
  const [modalStatus, setModalStatus] = useState(false)
  const [uploadZipFilePath, setUploadZipFilePath] = useState<string | null>(null)

  const getFolderPathString = useCallback(
    (
      path: { id: string; name: string }[],
      parent: { id: string; name: string } | null,
      fileName?: string
    ) => {
      const fullPath = parent ? [...path, parent] : path
      let uniquePath = Array.from(new Set(fullPath.map((folder) => folder.name)))

      if (fileName) {
        uniquePath.push(fileName)
      }
      return uniquePath.join('/')
    },
    []
  )

  const StorageCard: StorageType[] = [
    {
      id: 1,
      title: 'Google Drive',
      description: `${isReconnectClicked ? 'Reconnect' : 'Connect'
        } Google Drive to Load Files and assets to ${brandingConfig.brandName}`,
      icon: '/media/icons/RecordsKeeperAI/ic-gdrive.svg',
      type: RecordSync.GOOGLE_DRIVE,
    },
    {
      id: 2,
      title: 'Dropbox',
      description: `${isReconnectClicked ? 'Reconnect' : 'Connect'
        } Dropbox to load files, records and assets to ${brandingConfig.brandName}`,
      icon: '/media/icons/RecordsKeeperAI/ic-onedrive.svg',
      type: RecordSync.DROPBOX,
    },
    {
      id: 3,
      title: 'Microsoft OneDrive',
      description: `${isReconnectClicked ? 'Reconnect' : 'Connect'
        } Microsoft OneDrive to load files, records and assets to ${brandingConfig.brandName}`,
      icon: '/media/icons/RecordsKeeperAI/ic-mscloud.svg',
      type: RecordSync.ONEDRIVE,
    },
    {
      id: 4,
      title: 'Amazon (AWS) S3',
      description: `Connect Amazon S3 to load files, records and assets to ${brandingConfig.brandName}`,
      icon: '/media/icons/RecordsKeeperAI/ic-amazon-s3.svg',
      type: RecordSync.AWS_S3,
    },
    {
      id: 5,
      title: 'Google Cloud Storage',
      description: `Connect GCS Bucket to load files, records and assets to ${brandingConfig.brandName}`,
      icon: '/media/icons/RecordsKeeperAI/ic-gcloud.svg',
      type: RecordSync.GOOGLE_BUCKET,
    },
    {
      id: 6,
      title: 'Microsoft Azure',
      description: `Connect MS Azure storage bucket to load files, records and assets to ${brandingConfig.brandName}`,
      icon: '/media/icons/RecordsKeeperAI/ic-azure.svg',
      type: RecordSync.AZURE_BLOB,
    },
    {
      id: 7,
      title: 'Zip File from Local Storage',
      description: `Connect Zip File from Local Storage to load files, records and assets to ${brandingConfig.brandName}`,
      icon: '/media/icons/RecordsKeeperAI/ic-ext-zip.svg',
      type: RecordSync.ZIP_FILE,
    },
  ]

  const initialValues = {
    storageName: storageDataName || null,
  }

  const storageSchemas = [
    Yup.object().shape({
      storageName: Yup.string()
        .nullable()
        .when([], {
          is: () => !isReconnectClicked,
          then: Yup.string().nullable().required('Storage name is required'),
          otherwise: Yup.string().nullable(),
        }),
    }),
    Yup.object().shape({
      accessKey: Yup.string().nullable().required('Aws access key is required'),
      accessSecretKey: Yup.string().nullable().required('Aws secret key is required'),
      cloudRegion: Yup.object().nullable().required('Aws Region is required'),
      cloudBucketName: Yup.string().nullable().required('Aws s3 bucket name is required'),
      storageName: Yup.string().nullable().required('Storage name is required'),
    }),

    Yup.object().shape({
      connectionString: Yup.string().nullable().required('Connection string is required'),
      cloudBucketName: Yup.string().nullable().required('Azure blob bucket name is required'),
      storageName: Yup.string().nullable().required('Storage name is required'),
    }),
    Yup.object().shape({
      clientEmail: Yup.string()
        .email('Invalid email address')
        .nullable()
        .required('Client email is required'),
      privateKey: Yup.string().nullable().required('Private key is required'),
      projectId: Yup.string().nullable().required('Project id is required'),
      cloudBucketName: Yup.string().nullable().required('Google storage bucket name is required'),
      storageName: Yup.string().nullable().required('Storage name is required'),
    }),
  ]

  const [currentSchema, setCurrentSchema] = useState(storageSchemas[0])

  const onClose = () => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('code')
    newParams.delete('scope')
    newParams.delete('authuser')
    newParams.delete('prompt')
    setSearchParams(newParams)
    sessionStorage.removeItem('recordsKeeperSourceSelectedCardId')
    sessionStorage.removeItem('recordsKeeperSourceStorageName')
    sessionStorage.removeItem('recordsKeeperSourceId')
    sessionStorage.removeItem('reconnectSourceId')
    sessionStorage.removeItem('reconnectSourceType')
    sessionStorage.removeItem('reconnectSourceName')
    sessionStorage.removeItem('isReconnectClicked')
    setIsReconnectClicked(false)
    setCurrentDocumentStep(1)
    setSourceId(null)
    setUploadZipFilePath(null)
    setSelectedCard(undefined)
    setStorageDataName(undefined)
    setOpenedFolderId('rootFolder')
    if (formikRef.current) {
      formikRef.current.resetForm()
    }
    setIsActive(false)
  }

  const handleRecordKeeperFilesSync = async () => {
    setIsLoading(true)
    if (!selectedFileOrFolderId || !selectedCard?.type || !sourceId) return
    if (!companyId) return
    try {
      const folderPathString = openedFolderId
        ? getFolderPathString(
          folderPath,
          parentFolder,
          selectedFileOrFolderId.isFolder ? undefined : selectedFileOrFolderId?.name
        )
        : selectedFileOrFolderId?.name

      const {
        data: { success, errors },
      } = await selectedFilesToSyncRecordsKeeper(
        sourceId,
        selectedCard?.type,
        selectedFileOrFolderId,
        companyId,
        folderPathString
      )
      if (success) {
        toast.success(formatMessage({ id: 'Source connected successfully with RecordsKeeper' }))
        setSelectedFileOrFolderId(null)
        setFolderPath([])
        setParentFolder(null)
        setOpenedFolderId('rootFolder')
        fetchSources()
        onClose()
      } else {
        toast.error(errors[0])
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (code) {
      const sessionStorageSelectedCardId = sessionStorage.getItem(
        'recordsKeeperSourceSelectedCardId'
      )
      const isReconnectClicked = !!sessionStorage.getItem('isReconnectClicked')
      if (sessionStorageSelectedCardId) {
        setIsActive(true)
        setSelectedCard(
          StorageCard.find((card) => card.id === parseInt(sessionStorageSelectedCardId))
        )
        setCurrentDocumentStep(2)
        const sessionStorageStorageName = sessionStorage.getItem('recordsKeeperSourceStorageName')
        const sessionStorageSourceId = sessionStorage.getItem('recordsKeeperSourceId')

        if (sessionStorageStorageName) {
          setStorageDataName(sessionStorageStorageName)
        }
        if (sessionStorageSourceId) {
          setSourceId(sessionStorageSourceId)
        }
      } else if (isReconnectClicked) {
        setIsReconnectClicked(true)
        const reconnectSourceId = sessionStorage.getItem('reconnectSourceId')
        const reconnectSourceType = sessionStorage.getItem('reconnectSourceType')
        const reconnectSourceName = sessionStorage.getItem('reconnectSourceName')
        console.log(reconnectSourceType)

        setIsActive(true)
        if (reconnectSourceId && reconnectSourceType && reconnectSourceName) {
          setStorageDataName(reconnectSourceName)
          setSelectedCard(
            StorageCard.find((card) => card.type === reconnectSourceType) as StorageType
          )
          setCurrentDocumentStep(2)
          setSourceId(reconnectSourceId)
        }
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCardClick = (storage: StorageType, formik: any) => {
    if (selectedCard?.id !== storage.id) {
      if (OauthIntegrationTypes.includes(storage.type)) {
        const newParams = new URLSearchParams(searchParams)
        newParams.delete('code')
        newParams.delete('scope')
        newParams.delete('authuser')
        newParams.delete('prompt')
        setSearchParams(newParams)
        sessionStorage.removeItem('recordsKeeperSourceSelectedCardId')
        sessionStorage.removeItem('recordsKeeperSourceStorageName')
        sessionStorage.removeItem('recordsKeeperSourceId')
        formik.setFieldValue('storageName', null)
      } else if (CloudStorageIntegrationTypes.includes(storage.type) && formikRef.current) {
        formikRef.current.resetForm()
      }
      setSelectedCard(storage)
    }
  }

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    select_account: true,
    ux_mode: 'redirect',
    redirect_uri: process.env.REACT_APP_DROPBOX_REDIRECT_URI as string,
    scope:
      'https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/drive.file',
  })

  const handleDropboxLogin = async () => {
    const redirectUri = process.env.REACT_APP_DROPBOX_REDIRECT_URI as string
    const clientId = process.env.REACT_APP_DROPBOX_CLIENT_ID as string

    try {
      if (!isReconnectClicked) {
        sessionStorage.setItem(
          'recordsKeeperSourceSelectedCardId',
          (selectedCard?.id as number).toString()
        )
        if (formikRef.current && formikRef.current.values.storageName) {
          sessionStorage.setItem(
            'recordsKeeperSourceStorageName',
            formikRef.current.values.storageName
          )
        }
      }

      // Generate the authorization URL
      const authUrl = `https://www.dropbox.com/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&token_access_type=offline`

      // Redirect the user to the authUrl
      window.location.href = (await authUrl).toString()
    } catch (error) {
      console.error('Error authenticating with Dropbox:', error)
    }
  }

  const handleOneDriveLogin = async () => {
    const clientId = process.env.REACT_APP_MICOSOFT_ONEDRIVE_CLIENT_ID as string
    const redirectUri = process.env.REACT_APP_DROPBOX_REDIRECT_URI

    if (!clientId || !redirectUri) {
      console.error('Missing required environment variables')
      return
    }

    try {
      if (!isReconnectClicked) {
        sessionStorage.setItem(
          'recordsKeeperSourceSelectedCardId',
          (selectedCard?.id as number).toString()
        )
        if (formikRef.current && formikRef.current.values.storageName) {
          sessionStorage.setItem(
            'recordsKeeperSourceStorageName',
            formikRef.current.values.storageName
          )
        }
      }

      // Use 'common' endpoint to allow any Microsoft account
      const authUrl =
        `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
        new URLSearchParams({
          client_id: clientId,
          response_type: 'code',
          redirect_uri: redirectUri,
          scope: 'openid profile Files.Read Files.Read.All User.Read offline_access',
          prompt: 'select_account', // Allows user to choose their account
        }).toString()

      window.location.href = authUrl
    } catch (error) {
      console.error('Error authenticating with Onedrive:', error)
    }
  }

  const handleSubmit = (values: any) => {
    setIsActive(false) // Close modal on submit
  }

  const nextStep = async () => {
    if (currentDocumentStep < 3) {
      if (currentDocumentStep === 2) {
        let payload
        console.log(isReconnectClicked, selectedCard, 'code=====>', code)
        if (
          selectedCard &&
          OauthIntegrationTypes.includes(selectedCard?.type) &&
          !isReconnectClicked
        ) {
          const storageName = formikRef.current.values.storageName
          if (!code || !storageName) return
          if (sourceId) {
            payload = { storageName, integrationToken: code }
          } else {
            payload = { storageName, integrationToken: code, sourceName: selectedCard?.type }
          }
        } else if (isReconnectClicked) {
          if (selectedCard && OauthIntegrationTypes.includes(selectedCard?.type)) {
            if (!code) return
            if (sourceId) {
              const storageName = storageDataName
              console.log(code, 'code=====>')

              payload = { storageName, integrationToken: code }
            }
          }
        } else if (
          selectedCard &&
          CloudStorageIntegrationTypes.includes(selectedCard?.type) &&
          formikRef.current
        ) {
          if (
            selectedCard?.type === RecordSync.AWS_S3 &&
            !formikRef.current.values.awsAccessKey &&
            !formikRef.current.values.accessSecretKey &&
            !formikRef.current.values.cloudRegion.value &&
            !formikRef.current.values.cloudBucketName &&
            !formikRef.current.values.storageName
          ) {
            return
          } else if (
            selectedCard?.type === RecordSync.AZURE_BLOB &&
            !formikRef.current.values.connectionString &&
            !formikRef.current.values.cloudBucketName &&
            !formikRef.current.values.storageName
          ) {
            return
          } else if (
            selectedCard?.type === RecordSync.AZURE_BLOB &&
            !formikRef.current.values.clientEmail &&
            !formikRef.current.values.privateKey &&
            !formikRef.current.values.projectId &&
            !formikRef.current.values.cloudBucketName &&
            !formikRef.current.values.storageName
          ) {
            return
          }
          if (selectedCard?.type === RecordSync.AWS_S3) {
            payload = {
              ...formikRef.current.values,
              cloudRegion: formikRef.current.values.cloudRegion.value,
              sourceName: selectedCard?.type,
            }
          } else {
            payload = { ...formikRef.current.values, sourceName: selectedCard?.type }
          }
        } else if (
          selectedCard &&
          selectedCard?.type === RecordSync.ZIP_FILE &&
          formikRef.current &&
          uploadZipFilePath
        ) {
          const storageName = formikRef.current.values.storageName
          if (sourceId) {
            payload = { storageName, zipFilePath: uploadZipFilePath }
          } else {
            payload = { storageName, zipFilePath: uploadZipFilePath, sourceName: selectedCard?.type }
          }
        }
        if (sourceId) {
          if (!selectedCard) return
          setIsLoading(true)
          try {
            const {
              data: { success, data, errors },
            } = await updateSourceSync(sourceId, payload, selectedCard?.type, companyId!)
            if (success) {
              setSourceId(data.sourceId)
              if (isReconnectClicked) {
                const newParams = new URLSearchParams(searchParams)
                newParams.delete('code')
                newParams.delete('scope')
                newParams.delete('authuser')
                newParams.delete('prompt')
                setSearchParams(newParams)
                sessionStorage.removeItem('reconnectSourceId')
                sessionStorage.removeItem('reconnectSourceType')
                sessionStorage.removeItem('reconnectSourceName')
                sessionStorage.removeItem('isReconnectClicked')
                setIsReconnectClicked(false)
                setSelectedCard(undefined)
                setStorageDataName(undefined)
                setIsActive(false)
                fetchSources()
              } else {
                setCurrentDocumentStep(currentDocumentStep + 1)
              }
            } else {
              toast.error(errors[0])
            }
          } catch (error) {
            console.log(error)
          } finally {
            setIsLoading(false)
          }
        } else {
          if (!selectedCard) return
          setIsLoading(true)
          try {
            const {
              data: { success, data, errors },
            } = await createSourceSync(payload, companyId!)
            if (success) {
              setSourceId(data.sourceId)
              sessionStorage.setItem('recordsKeeperSourceId', data.sourceId)
              setCurrentDocumentStep(currentDocumentStep + 1)
            } else {
              toast.error(errors[0])
            }
          } catch (error) {
            console.log(error)
          } finally {
            setIsLoading(false)
          }
        }
      } else {
        if (currentDocumentStep === 1) {
          if (selectedCard?.type === RecordSync.AWS_S3) {
            await setCurrentSchemaAsync(storageSchemas[1])
          } else if (selectedCard?.type === RecordSync.AZURE_BLOB) {
            await setCurrentSchemaAsync(storageSchemas[2])
          } else if (selectedCard?.type === RecordSync.GOOGLE_BUCKET) {
            await setCurrentSchemaAsync(storageSchemas[3])
          } else if (selectedCard?.type === RecordSync.ZIP_FILE) {
            await setCurrentSchemaAsync(storageSchemas[0])
          }
          if (formikRef.current) {
            await formikRef.current.validateForm()
          }
        }
        setCurrentDocumentStep(currentDocumentStep + 1)
      }

      setFolderPath([])
      setOpenedFolderId('rootFolder')
      setSelectedFileOrFolderId(null)
      setParentFolder(null)
    }
  }

  const setCurrentSchemaAsync = async (newSchema: any) => {
    return new Promise<void>((resolve) => {
      setCurrentSchema(newSchema)
      resolve()
    })
  }

  const prevStep = async () => {
    if (currentDocumentStep > 1) {
      if (currentDocumentStep === 3) {
        if (selectedCard?.type === RecordSync.AWS_S3) {
          await setCurrentSchemaAsync(storageSchemas[1])
        } else if (selectedCard?.type === RecordSync.AZURE_BLOB) {
          await setCurrentSchemaAsync(storageSchemas[2])
        } else if (selectedCard?.type === RecordSync.GOOGLE_BUCKET) {
          await setCurrentSchemaAsync(storageSchemas[3])
        }
      }
      setCurrentDocumentStep(currentDocumentStep - 1)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={currentSchema}
      enableReinitialize={true}
      validateOnMount={true}
      validateOnChange={true}
      onSubmit={handleSubmit}
      innerRef={formikRef}
    >
      {(formik) => {
        return (
          <Form>
            <Modal
              show={isActive}
              onHide={onClose}
              size='lg'
              fullscreen
              className={currentDocumentStep === 3 ? 'storage-modal' : ''}
            >
              <Modal.Header closeButton>
                <h2>{formatMessage({ id: 'Link Cloud Storage' })}</h2>
              </Modal.Header>
              <Modal.Body>
                <div
                  className='stepper stepper-pills stepper-column d-flex flex-column container'
                  id='kt_create_account_stepper'
                >
                  <div className='d-flex justify-content-start flex-column w-100'>
                    <div className='card-body px-0 d-flex justify-content-start'>
                      <div
                        className='d-flex gap-6 align-items-center'
                        style={{
                          overflowX: 'auto',
                          whiteSpace: 'nowrap',
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none',
                        }}
                      >
                        {isReconnectClicked ? (
                          <div
                            className={`stepper-item current me-6 mb-4`}
                            style={{ display: 'inline-block' }}
                          >
                            <div className='stepper-wrapper'>
                              <div className='stepper-icon w-40px h-40px'>
                                <i className='stepper-check'></i>
                                <span className='stepper-number'>1</span>
                              </div>
                              <div className='stepper-label'>
                                <h3 className='stepper-title fw-semibold font-size-13'>
                                  {formatMessage({
                                    id: 'Reconnect',
                                  })}
                                </h3>
                                <div className='stepper-desc font-size-12'>
                                  {formatMessage({
                                    id: 'Reconnect Storage',
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            {[1, 2, 3].map((step) => (
                              <div
                                key={step}
                                className={`stepper-item ${currentDocumentStep === step ? 'current' : ''
                                  } me-6 mb-4`}
                                style={{ display: 'inline-block' }}
                              >
                                <div className='stepper-wrapper'>
                                  <div className='stepper-icon w-40px h-40px'>
                                    <i className='stepper-check'></i>
                                    <span className='stepper-number'>{step}</span>
                                  </div>
                                  <div className='stepper-label'>
                                    <h3 className='stepper-title fw-semibold font-size-13'>
                                      {formatMessage({
                                        id:
                                          step === 1
                                            ? 'Storage'
                                            : step === 2
                                              ? 'Connect'
                                              : 'Directory',
                                      })}
                                    </h3>
                                    <div className='stepper-desc font-size-12'>
                                      {formatMessage({
                                        id:
                                          step === 1
                                            ? 'Select Storage'
                                            : step === 2
                                              ? 'Connect Storage'
                                              : 'Storage Verification',
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='d-flex flex-column justify-content-between'>
                    <div className=''>
                      {/* Step 1  */}
                      {currentDocumentStep === 1 && !isReconnectClicked && (
                        <div className='w-100'>
                          <InfoCard2
                            title={formatMessage({
                              id: 'Connecting Cloud or Local Storage to RecordsKeeper',
                            })}
                            desc={formatMessage({
                              id: 'Use this page to connect your preferred cloud storage accounts or upload local .zip file to RecordsKeeper. Select a storage option to begin syncing your files and records seamlessly. You can add multiple cloud storage services for better organization and accessibility.',
                            })}
                            slug='https://help.recordskeeper.ai/en/articles/10368848-how-to-add-and-connect-cloud-storage-in-recordskeeper-ai'
                            className='w-100 mb-0'
                          />

                          <Row className='mt-4'>
                            {StorageCard.map((card) => (
                              <Col md={4} key={card.id}>
                                <div
                                  role='button'
                                  className={`bg-white rounded-2 p-4 cursor-pointer border mb-4 ${selectedCard?.id === card.id
                                    ? 'border border-primary bg-light'
                                    : ''
                                    } ${currentDocumentStep === 1 &&
                                      !!sourceId &&
                                      card.id !== selectedCard?.id
                                      ? 'opacity-50 pointer-events-none'
                                      : 'cursor-pointer'
                                    }`}
                                  aria-disabled={currentDocumentStep === 1 && !!sourceId}
                                  onClick={() => {
                                    if (!sourceId) {
                                      handleCardClick(card, formik)
                                    }
                                  }}
                                >
                                  <div className='d-flex mb-4 justify-content-between'>
                                    <img src={card.icon} alt='' width={48} height={48} />
                                    {selectedCard?.id === card.id && (
                                      <img
                                        src={toAbsoluteUrl(
                                          '/media/icons/RecordsKeeperAI/ic-tick.svg'
                                        )}
                                        alt=''
                                        width={20}
                                        height={20}
                                      />
                                    )}
                                  </div>
                                  <div>
                                    <h3 className='mb-2'>{card.title}</h3>
                                    <p className='m-0'>{card.description}</p>
                                  </div>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      )}

                      {/* Step 2  */}
                      {currentDocumentStep === 2 && (
                        <div className='w-100'>
                          <InfoCard2
                            title={formatMessage({
                              id: 'Connecting Your Cloud Storage',
                            })}
                            desc={formatMessage({
                              id: 'In this step, you can securely connect your cloud storage account to RecordsKeeper. Enter a custom storage name for easy identification, and choose whether to connect via your account login or using an API key. Validate your connection to proceed.',
                            })}
                            slug='https://help.recordskeeper.ai/en/articles/10368848-how-to-add-and-connect-cloud-storage-in-recordskeeper-ai'
                            className='w-100 mb-0'
                          />

                          <div className='mt-4 bg-white border border-grey rounded-2 p-4'>
                            <div className='mb-6'>
                              <img
                                src={
                                  selectedCard?.icon
                                    ? selectedCard?.icon
                                    : StorageCard.find(
                                      (storage) => storage.type === reconnectSourceIsClicked
                                    )?.icon
                                }
                                alt=''
                                width={48}
                                height={48}
                                className='mb-4'
                              />
                              <h3 className='mb-2'>
                                {selectedCard?.title
                                  ? selectedCard?.title
                                  : StorageCard.find(
                                    (storage) => storage.type === reconnectSourceIsClicked
                                  )?.title}
                              </h3>
                              <p className='m-0 text-muted'>
                                {selectedCard?.description
                                  ? selectedCard?.description
                                  : StorageCard.find(
                                    (storage) => storage.type === reconnectSourceIsClicked
                                  )?.description}
                              </p>
                            </div>

                            {!isReconnectClicked && (
                              <div className='mb-6'>
                                <div className='w-100 w-md-50'>
                                  <TextInput
                                    fieldType='text'
                                    label={formatMessage({ id: 'Storage Name' })}
                                    fieldName='storageName'
                                    formik={formik}
                                    placeholder={formatMessage({ id: 'Enter Storage Name' })}
                                    toolTipText={formatMessage({
                                      id: 'Enter a custom name for this storage location to easily identify it within the RecordsKeeper system.',
                                    })}
                                    isStarRequired={true}
                                    isTooltipForModal={true}
                                    onBlur={formik.handleBlur}
                                  />
                                </div>
                              </div>
                            )}

                            {((selectedCard &&
                              OauthIntegrationTypes.includes(selectedCard?.type)) ||
                              StorageCard.find(
                                (storage) => storage.type === reconnectSourceIsClicked
                              )) && (
                                <div className='mb-6'>
                                  <h4 className='mb-4'>
                                    {`${isReconnectClicked
                                      ? formatMessage({ id: 'Reconnect Using' })
                                      : formatMessage({ id: 'Connect Using' })
                                      } ${selectedCard?.title
                                        ? selectedCard?.title
                                        : StorageCard.find(
                                          (storage) => storage.type === reconnectSourceIsClicked
                                        )?.title
                                      }`}
                                  </h4>

                                  <button
                                    type='button'
                                    id='googleConnectButton'
                                    className='btn btn-lg rounded d-flex justify-content-center w-md-50 w-100'
                                    onClick={() => {
                                      if (selectedCard?.id === 1) {
                                        sessionStorage.setItem(
                                          'recordsKeeperSourceSelectedCardId',
                                          (selectedCard?.id as number).toString()
                                        )
                                        if (
                                          formikRef.current &&
                                          formikRef.current.values.storageName
                                        ) {
                                          sessionStorage.setItem(
                                            'recordsKeeperSourceStorageName',
                                            formikRef.current.values.storageName
                                          )
                                        }
                                        googleLogin()
                                      } else if (selectedCard?.id === 2) {
                                        handleDropboxLogin()
                                      } else if (selectedCard?.id === 3) {
                                        handleOneDriveLogin()
                                      } else {
                                        sessionStorage.setItem(
                                          'reconnectSourceId',
                                          reconnectSourceIdIsClicked as string
                                        )
                                        sessionStorage.setItem(
                                          'reconnectSourceType',
                                          reconnectSourceIsClicked as RecordSync
                                        )
                                        sessionStorage.setItem(
                                          'reconnectSourceName',
                                          reconnectSourceNameIsClicked as string
                                        )

                                        sessionStorage.setItem(
                                          'isReconnectClicked',
                                          isReconnectClicked.toString()
                                        )
                                        if (reconnectSourceIsClicked === RecordSync.GOOGLE_DRIVE) {
                                          googleLogin()
                                        } else if (reconnectSourceIsClicked === RecordSync.DROPBOX) {
                                          handleDropboxLogin()
                                        } else if (reconnectSourceIsClicked === RecordSync.ONEDRIVE) {
                                          handleOneDriveLogin()
                                        }
                                      }
                                    }}
                                    style={{
                                      border: '1px solid #4776E6',
                                      color: '#4776E6',
                                    }}
                                  >
                                    <img
                                      src={
                                        selectedCard?.icon
                                          ? selectedCard?.icon
                                          : StorageCard.find(
                                            (storage) => storage.type === reconnectSourceIsClicked
                                          )?.icon
                                      }
                                      alt='Google'
                                      height={21}
                                      width={21}
                                      style={{ marginRight: '8px' }}
                                    />
                                    <div className='m-0 connected-acc-button-text'>
                                      {code ? (
                                        <div>
                                          {isReconnectClicked
                                            ? formatMessage({ id: 'Reconnected' })
                                            : formatMessage({ id: 'Connected' })}

                                          <TiTick className='ms-2 font-size-16 text-success' />
                                        </div>
                                      ) : (
                                        <>
                                          {isReconnectClicked
                                            ? formatMessage({ id: 'Reconnect Now' })
                                            : formatMessage({ id: 'Connect Now' })}
                                        </>
                                      )}
                                    </div>
                                  </button>
                                </div>
                              )}

                            {selectedCard && selectedCard?.type === RecordSync.AWS_S3 && (
                              <div className='mb-6'>
                                <h4 className='mb-4'>
                                  {`${formatMessage({ id: 'Connect Using' })} ${selectedCard?.title}`}
                                </h4>

                                <div className='mb-6 row '>
                                  <div className='col-6'>
                                    <TextInput
                                      fieldType='text'
                                      label={formatMessage({ id: 'Access Key' })}
                                      fieldName='accessKey'
                                      formik={formik}
                                      placeholder={formatMessage({ id: 'Enter the access key' })}
                                      toolTipText={formatMessage({
                                        id: 'Enter your AWS user access key ID from AWS Security Credentials.',
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>

                                  <div className='col-6'>
                                    <TextInput
                                      fieldType='password'
                                      label={formatMessage({ id: 'Access Secret Key' })}
                                      fieldName='accessSecretKey'
                                      formik={formik}
                                      placeholder={formatMessage({
                                        id: 'Enter the access secret key',
                                      })}
                                      toolTipText={formatMessage({
                                        id: 'Enter your AWS user secret access key from AWS Security Credentials.',
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>

                                  <div className='col-6'>
                                    <SelectSearchInput
                                      label={formatMessage({ id: 'Storage Cloud Region' })}
                                      fieldName='cloudRegion'
                                      placeholder={formatMessage({ id: 'Storage Cloud Region' })}
                                      formik={formik}
                                      toolTipText={formatMessage({
                                        id: 'Select the AWS region where your S3 bucket is hosted (e.g., us-east-1).',
                                      })}
                                      options={cloudRegionTypes}
                                      isTooltipForModal={true}
                                      isStarRequired={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>

                                  <div className='col-6'>
                                    <TextInput
                                      fieldType='text'
                                      label={formatMessage({ id: 'Bucket Name' })}
                                      fieldName='cloudBucketName'
                                      formik={formik}
                                      placeholder={formatMessage({ id: 'Enter the Bucket Name' })}
                                      toolTipText={formatMessage({
                                        id: 'Enter the exact name of your AWS S3 bucket to connect successfully.',
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {selectedCard && selectedCard?.type === RecordSync.AZURE_BLOB && (
                              <div className='mb-6'>
                                <h4 className='mb-4'>
                                  {`${formatMessage({ id: 'Connect Using' })} ${selectedCard?.title}`}
                                </h4>

                                <div className='mb-6 row '>
                                  <div className='col-12'>
                                    <TextArea
                                      label={formatMessage({ id: 'Connection String' })}
                                      fieldName='connectionString'
                                      formik={formik}
                                      placeholder={formatMessage({
                                        id: 'Enter MS Azure Connection String',
                                      })}
                                      toolTipText={formatMessage({
                                        id: `Enter the connection string found in your Azure Storage Account's 'Access Keys' section.`,
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>

                                  <div className='col-12'>
                                    <TextInput
                                      fieldType='text'
                                      label={formatMessage({ id: 'Bucket Name' })}
                                      fieldName='cloudBucketName'
                                      formik={formik}
                                      placeholder={formatMessage({
                                        id: 'Enter MS Azure Bucket Name',
                                      })}
                                      toolTipText={formatMessage({
                                        id: 'Enter the name of your Azure Storage Container (often called bucket or blob container).',
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {selectedCard && selectedCard?.type === RecordSync.GOOGLE_BUCKET && (
                              <div className='mb-6'>
                                <h4 className='mb-4'>
                                  {`${formatMessage({ id: 'Connect Using' })} ${selectedCard?.title}`}
                                </h4>

                                <div className='mb-6 row '>
                                  <div className='col-6'>
                                    <TextInput
                                      fieldType='text'
                                      label={formatMessage({ id: 'Project Id' })}
                                      fieldName='projectId'
                                      formik={formik}
                                      placeholder={formatMessage({
                                        id: 'Enter the google storage project id',
                                      })}
                                      toolTipText={formatMessage({
                                        id: 'Enter the Project ID as it is required to establish a secure connection between RecordsKeeper.AI and your Google Cloud Storage.',
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>

                                  <div className='col-6'>
                                    <TextInput
                                      fieldType='email'
                                      label={formatMessage({ id: 'Client Email' })}
                                      fieldName='clientEmail'
                                      formik={formik}
                                      placeholder={formatMessage({
                                        id: 'Enter the Google Storage Client Email',
                                      })}
                                      toolTipText={formatMessage({
                                        id: 'Enter the service account email address generated in your Google Cloud Console.',
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>

                                  <div className='col-12'>
                                    <TextArea
                                      label={formatMessage({ id: 'Private Key' })}
                                      fieldName='privateKey'
                                      formik={formik}
                                      placeholder={formatMessage({
                                        id: 'Enter the Google Storage Private Key',
                                      })}
                                      toolTipText={formatMessage({
                                        id: 'Paste your Google Cloud service account private key here. This key is essential for secure authentication.',
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>

                                  <div className='col-12'>
                                    <TextInput
                                      fieldType='text'
                                      label={formatMessage({ id: 'Bucket Name' })}
                                      fieldName='cloudBucketName'
                                      formik={formik}
                                      placeholder={formatMessage({
                                        id: 'Enter the Google Storage Bucket Name',
                                      })}
                                      toolTipText={formatMessage({
                                        id: 'Enter the exact name of your Google Cloud Storage bucket that contains your documents.',
                                      })}
                                      isStarRequired={true}
                                      isTooltipForModal={true}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {selectedCard && selectedCard?.type === RecordSync.ZIP_FILE && (
                              <div className='mb-6'>
                                <h4 className='mb-4'>
                                  {`${formatMessage({ id: 'Extract Files Using' })} ${selectedCard?.title
                                    }`}
                                </h4>

                                <button
                                  type='button'
                                  id='googleConnectButton'
                                  className='btn btn-lg rounded d-flex justify-content-center w-50'
                                  onClick={() => {
                                    if (selectedCard?.id === 7) {
                                      setModalStatus(true)
                                    }
                                  }}
                                  style={{
                                    border: '1px solid #4776E6',
                                    color: '#4776E6',
                                  }}
                                >
                                  <img
                                    src={
                                      selectedCard?.icon
                                        ? selectedCard?.icon
                                        : StorageCard.find(
                                          (storage) => storage.type === reconnectSourceIsClicked
                                        )?.icon
                                    }
                                    alt='Google'
                                    height={21}
                                    width={21}
                                    style={{ marginRight: '8px' }}
                                  />
                                  <div className='m-0 connected-acc-button-text'>
                                    {uploadZipFilePath ? (
                                      <div>
                                        {formatMessage({ id: 'Added ZIP file' })}
                                        <TiTick className='ms-2 font-size-16 text-success' />
                                      </div>
                                    ) : (
                                      formatMessage({ id: 'Add ZIP file' })
                                    )}
                                  </div>
                                </button>
                                <FileUpload
                                  fileSize={parseInt(
                                    process.env.REACT_APP_RK_MAX_ZIP_FILE_SIZE as string
                                  )}
                                  maxFileNumber={1}
                                  allowType={[
                                    'application/zip',
                                    'application/x-zip',
                                    'application/x-zip-compressed',
                                    'application/vnd.rar',
                                    'application/x-rar-compressed',
                                  ]}
                                  metaData={{ module: 'document', isProtected: false }}
                                  modalStatus={modalStatus}
                                  handleClose={() => {
                                    setModalStatus(false)
                                  }}
                                  handleSuccess={(id: number, name: string) => {
                                    setUploadZipFilePath(name)
                                  }}
                                  resourceType='all'
                                  assemblyOption='rk-doc-upload'
                                  isZipFile={true}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Step 3  */}
                      {currentDocumentStep === 3 && !isReconnectClicked && (
                        <div className='w-100'>
                          <InfoCard2
                            title={formatMessage({
                              id: 'Select Directory or Folder for Syncing',
                            })}
                            desc={formatMessage({
                              id: `In this step, choose the directory or folder from your connected storage that you want to sync with RecordsKeeper. You can browse your files and select specific folders for efficient organization and management. Once you've selected the directory, click "Add to RecordKeeper" to complete the process.`,
                            })}
                            slug='https://help.recordskeeper.ai/en/articles/10369676-how-to-sync-directories-and-folders-with-recordskeeper-ai'
                            className='w-100 mb-0'
                          />
                          {sourceId && selectedCard?.type && (
                            <StorageDirectoryTable
                              sourceType={selectedCard.type}
                              sourceId={sourceId}
                              selectedFileOrFolderId={selectedFileOrFolderId}
                              setSelectedFileOrFolderId={setSelectedFileOrFolderId}
                              setParentFolder={setParentFolder}
                              folderPath={folderPath}
                              setFolderPath={setFolderPath}
                              openedFolderId={openedFolderId}
                              setOpenedFolderId={setOpenedFolderId}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer className='container px-0'>
                <div className='d-md-flex w-100 w-md-auto justify-content-end gap-3'>
                  <div className='w-100 order-1 order-md-2 mb-2 mb-md-auto'>
                    {currentDocumentStep < 3 ? (
                      <button
                        type='button'
                        className='btn bg-primary text-white custom-button-property w-100'
                        onClick={nextStep}
                        disabled={
                          (currentDocumentStep === 1 && !selectedCard) ||
                          (currentDocumentStep === 2 &&
                            selectedCard &&
                            OauthIntegrationTypes.includes(selectedCard?.type) &&
                            (!code || !formik.isValid || isLoading)) ||
                          (currentDocumentStep === 2 &&
                            selectedCard &&
                            CloudStorageIntegrationTypes.includes(selectedCard?.type) &&
                            (!formik.isValid || isLoading)) ||
                          (currentDocumentStep === 2 &&
                            selectedCard &&
                            selectedCard?.type === RecordSync.ZIP_FILE &&
                            (!formik.isValid || isLoading))
                        }
                      >
                        {currentDocumentStep === 2 && isLoading ? (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({ id: 'Please wait...' })}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <span className='indicator-label font-size-13 p-0'>
                            {isReconnectClicked
                              ? formatMessage({ id: 'Reconnect' })
                              : formatMessage({ id: 'Next' })}
                          </span>
                        )}
                      </button>
                    ) : (
                      <button
                        type='submit'
                        className=' btn bg-primary text-white custom-button-property w-100'
                        disabled={!selectedFileOrFolderId || isLoading}
                        onClick={handleRecordKeeperFilesSync}
                      >
                        {currentDocumentStep === 3 && isLoading ? (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({ id: 'Please wait...' })}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <span className='indicator-label font-size-13 p-0'>
                            {formatMessage({ id: 'Add to RecordKeeper' })}
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                  {currentDocumentStep > 1 && !isReconnectClicked && (
                    <div className='w-100 w-md-auto order-2 order-md-1 '>
                      <button
                        onClick={prevStep}
                        type='button'
                        disabled={isLoading}
                        className='btn bg-light text-muted custom-button-property w-100'
                      >
                        {formatMessage({ id: 'Back' })}
                      </button>
                    </div>
                  )}
                </div>
              </Modal.Footer>
            </Modal>
          </Form>
        )
      }}
    </Formik>
  )
}

export default AddStorageModal
